import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CreditCard, Plus, Trash2, ShieldAlert, ShieldCheck, KeyRound, Pencil } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { v: "cash", l: "Espèces (aucune API)" },
  { v: "wave", l: "Wave Money" },
  { v: "orange_money", l: "Orange Money" },
  { v: "moov_money", l: "Moov Money" },
  { v: "paytech", l: "PayTech" },
  { v: "cinepay", l: "Ciné Pay" },
  { v: "fusion", l: "Fusion Money" },
  { v: "other", l: "Autre" },
];

export default function Payments() {
  const [pms, setPms] = useState([]);
  const load = () => api.get("/payment-methods").then((r) => setPms(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Moyens de paiement</h1>
          <p className="text-sm text-slate-500">Configurez vos passerelles Wave / Orange Money / PayTech / Ciné Pay avec leurs clés API</p>
        </div>
        <PMDialog trigger={<Button className="btn-pill bg-orange-500 hover:bg-orange-600" data-testid="add-pm-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau moyen</Button>} onSaved={load} />
      </div>

      <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm">
          <b>Règle V2 §7 :</b> le mode réel n'est activable que si la clé API est renseignée. Sans clé, le paiement reste en mode <b>TEST</b> et aucune transaction réelle n'est effectuée.
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pms.map((pm) => (
          <Card key={pm.id} data-testid={`pm-card-${pm.name}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-orange-100 dark:bg-orange-500/20 text-orange-500 grid place-items-center shrink-0"><CreditCard className="w-5 h-5" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{pm.name}</div>
                    <div className="text-xs text-slate-500">{PROVIDERS.find(p => p.v === pm.provider)?.l || pm.provider || "—"}</div>
                  </div>
                </div>
                <PMDialog editing={pm} trigger={<Button variant="ghost" size="icon" data-testid={`edit-pm-${pm.name}`}><Pencil className="w-4 h-4" /></Button>} onSaved={load} />
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                {pm.test_mode ? (
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 font-semibold">🧪 Mode test</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 font-semibold">⚡ Mode réel</span>
                )}
                <span className={`px-2 py-0.5 rounded-full font-semibold ${pm.active ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300" : "bg-slate-100 text-slate-500"}`}>{pm.active ? "Actif" : "Inactif"}</span>
                {pm.provider && pm.provider !== "cash" && (
                  pm.has_api_key
                    ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 font-semibold inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Clé configurée</span>
                    : <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300 font-semibold inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Aucune clé API</span>
                )}
              </div>

              {pm.has_api_key && (
                <div className="text-xs font-mono text-slate-500 bg-slate-50 dark:bg-slate-900/60 rounded-lg p-2 flex items-center gap-2">
                  <KeyRound className="w-3 h-3 shrink-0" />
                  <span className="truncate" data-testid={`pm-key-mask-${pm.name}`}>{pm.api_key_masked}</span>
                </div>
              )}

              <div className="text-xs text-slate-500">Frais: {pm.fee_percent}% · {pm.currency || "FCFA"}</div>

              <Button variant="outline" size="sm" className="w-full" onClick={async () => {
                if (!confirm(`Supprimer "${pm.name}" ?`)) return;
                await api.delete(`/payment-methods/${pm.id}`); toast.success("Supprimé"); load();
              }} data-testid={`delete-pm-${pm.name}`}><Trash2 className="w-4 h-4 mr-1" /> Supprimer</Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function PMDialog({ trigger, editing, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => editing ? {
    name: editing.name, provider: editing.provider || "wave", active: editing.active,
    fee_percent: editing.fee_percent, test_mode: editing.test_mode,
    api_key: "", secret_key: "", // never pre-fill secrets
    merchant_id: editing.merchant_id || "", endpoint: editing.endpoint || "", currency: editing.currency || "FCFA",
  } : {
    name: "", provider: "wave", active: true, fee_percent: 0, test_mode: true,
    api_key: "", secret_key: "", merchant_id: "", endpoint: "", currency: "FCFA",
  });

  const save = async () => {
    try {
      if (editing) await api.patch(`/payment-methods/${editing.id}`, form);
      else await api.post("/payment-methods", form);
      toast.success(editing ? "Modifié" : "Ajouté"); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? `Modifier "${editing.name}"` : "Nouveau moyen de paiement"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Nom affiché</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pm-form-name" placeholder="Ex: Wave Sénégal" /></div>
          <div className="col-span-2"><Label>Fournisseur</Label>
            <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
              <SelectTrigger data-testid="pm-form-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.provider !== "cash" && (
            <>
              <div className="col-span-2"><Label>Clé API {editing?.has_api_key && <span className="text-xs text-slate-500">(laissez vide pour conserver l'existante)</span>}</Label>
                <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} data-testid="pm-form-apikey" placeholder={editing?.has_api_key ? editing.api_key_masked : "sk_live_..."} /></div>
              <div className="col-span-2"><Label>Clé secrète (optionnel)</Label>
                <Input type="password" value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })} data-testid="pm-form-secret" placeholder={editing?.has_secret_key ? editing.secret_key_masked : "••••"} /></div>
              <div><Label>Merchant ID</Label><Input value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })} /></div>
              <div><Label>Devise</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
              <div className="col-span-2"><Label>Endpoint / URL API</Label><Input value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })} placeholder="https://api.wave.com/..." /></div>
            </>
          )}
          <div><Label>Frais (%)</Label><Input type="number" value={form.fee_percent} onChange={(e) => setForm({ ...form, fee_percent: Number(e.target.value) })} /></div>
          <div className="flex items-center justify-between p-2"><span className="text-sm">Actif</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pm-form-active" /></div>
          <div className="col-span-2 flex items-center justify-between p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
            <div className="text-sm">
              <div className="font-semibold">{form.test_mode ? "🧪 Mode test" : "⚡ Mode réel"}</div>
              <div className="text-xs text-slate-500">{form.test_mode ? "Aucune vraie transaction effectuée" : "Transactions réelles via le fournisseur"}</div>
            </div>
            <Switch checked={form.test_mode} onCheckedChange={(v) => setForm({ ...form, test_mode: v })} data-testid="pm-form-testmode" />
          </div>
          {!form.test_mode && !form.api_key && !editing?.has_api_key && form.provider !== "cash" && (
            <div className="col-span-2 text-xs text-red-600 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Clé API requise pour activer le mode réel</div>
          )}
        </div>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600 w-full mt-3" onClick={save} data-testid="pm-form-save">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}
